// Download the Sankey chart as a PNG: title, subtitle, chart and legend on the
// paper background, at 2x for sharp sharing.

type LegendItem = { label: string; color: string };

const PAPER = "#FBF9F5";
const TEXT_1 = "#5C3417";
const TEXT_3 = "#8A6A52";

// SVG drawn onto a canvas can't load web fonts, so embed them as data URLs.
// Falls back to the system font if anything fails (offline, blocked, ...).
async function embeddedFontCss(): Promise<string> {
  try {
    const cssUrl =
      "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600&family=IBM+Plex+Mono:wght@400&display=swap";
    let css = await (await fetch(cssUrl)).text();
    const urls = Array.from(new Set(css.match(/https:\/\/[^)]+\.woff2/g) || []));
    await Promise.all(
      urls.map(async (url) => {
        const blob = await (await fetch(url)).blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        css = css.split(url).join(dataUrl);
      })
    );
    return css;
  } catch {
    return "";
  }
}

// Image files also have to be inlined for the same reason.
async function toDataUrl(path: string): Promise<string | null> {
  try {
    const blob = await (await fetch(path)).blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

const escapeXml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export async function downloadSankeyPng({
  chart,
  chartWidth,
  chartHeight,
  title,
  subtitle,
  legend,
  filename,
}: {
  chart: SVGSVGElement;
  chartWidth: number;
  chartHeight: number;
  title: string;
  /** Mono line under the title; "made with [logo] Caramel" is appended. */
  subtitle: string;
  legend: LegendItem[];
  filename: string;
}) {
  const pad = 40;
  const headerH = 72;
  const legendH = 40;
  const width = chartWidth + pad * 2;
  const height = pad + headerH + chartHeight + legendH + pad;

  // Legend laid out in one row (wraps are unlikely with 7 short labels)
  let lx = 0;
  const legendSvg = legend
    .map((item) => {
      const w = 14 + item.label.length * 7 + 18;
      const g = `<g transform="translate(${lx},0)"><rect width="9" height="9" y="-8" rx="3" fill="${item.color}"/><text x="14" font-size="12" fill="${TEXT_3}">${escapeXml(item.label)}</text></g>`;
      lx += w;
      return g;
    })
    .join("");

  const [fontCss, logo] = await Promise.all([embeddedFontCss(), toDataUrl("/logo-solid.svg")]);

  // Subtitle is IBM Plex Mono: every character is 0.6em wide, so the logo can
  // sit right after the text without measuring.
  const caption = `${subtitle} · made with`;
  const captionEnd = pad + caption.length * 13 * 0.6;
  const brand = logo
    ? `<image href="${logo}" x="${captionEnd + 7}" y="${pad + 37}" width="17" height="17"/>
  <text x="${captionEnd + 29}" y="${pad + 50}" font-size="14" font-weight="600" fill="${TEXT_1}">Caramel</text>`
    : `<text x="${captionEnd + 8}" y="${pad + 50}" font-size="14" font-weight="600" fill="${TEXT_1}">Caramel</text>`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" font-family="'Space Grotesk', sans-serif">
  <style>${fontCss}</style>
  <rect width="100%" height="100%" fill="${PAPER}"/>
  <text x="${pad}" y="${pad + 24}" font-size="24" font-weight="600" letter-spacing="-0.5" fill="${TEXT_1}">${escapeXml(title)}</text>
  <text x="${pad}" y="${pad + 50}" font-size="13" fill="${TEXT_3}" font-family="'IBM Plex Mono', monospace">${escapeXml(caption)}</text>
  ${brand}
  <g transform="translate(${pad},${pad + headerH})">${chart.innerHTML}</g>
  <g transform="translate(${pad},${pad + headerH + chartHeight + 28})">${legendSvg}</g>
</svg>`;

  const scale = 2;
  const img = new Image();
  const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Could not render chart"));
      img.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported");
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0, width, height);

    const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!png) throw new Error("Could not create image");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(png);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  } finally {
    URL.revokeObjectURL(url);
  }
}
